import { filter, reduce } from 'lodash/collection';
import { isEmpty } from 'lodash/lang';
import { has } from 'lodash/object';
import moment from 'moment';

import { yearMonthDayFormat } from '../../../constants/dateFormat';
import { setIn } from '../../../services/immutable';

import {
  filterSitesByStudyIdsAndCanceledSsus,
  filterSitesByStudyIdsAndSsus,
  filterStudiesBasedOnSiteIds,
  filterTherapeuticAreasBasedOnSelectedStudyIds
} from './modeReportsFiltersService';
import { DATE_FORMAT } from './modeReportsServices';

export function reducer(state, action) {
  const { ssus, initialData, filters, selectedValues, lastSelectedData } = state || {};

  if (!ssus || !filters || !selectedValues || !initialData) return state;

  const reducers = {
    SET_SSUS: function() {
      return { ...state, ssus: action.payload };
    },

    INIT_DATA: function() {
      const modeParameters = prepareModeParameters(action.payload);
      return {
        ...state,
        ...modeParameters
      };
    },

    SET_DATA_FILTER_SITES_BY_CANCELED_SSUS: function() {
      const sites = filterSitesByStudyIdsAndCanceledSsus(
        action.payload.value.studies,
        action.payload.value.sites,
        ssus
      );
      return {
        ...state,
        initialData: { ...initialData, ...{ sites } },
        filters: { ...filters, ...{ sites } },
        selectedValues: { ...selectedValues, ...{ sites } }
      };
    },

    SET_DATE_TO_NOW: function() {
      const now = new moment().format(yearMonthDayFormat);
      const hasLastSelected = has(lastSelectedData, action.payload.filterKey);
      const currentValue = hasLastSelected ? selectedValues[action.payload.filterKey] : '';
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: now },
        filters: { ...filters, [action.payload.filterKey]: now },
        selectedValues: { ...selectedValues, [action.payload.filterKey]: hasLastSelected ? currentValue : now }
      };
    },

    SET_DATE_TO_EMPTY: function() {
      const emptyTime = '';
      const hasLastSelected = has(lastSelectedData, action.payload.filterKey);
      const currentValue = hasLastSelected ? selectedValues[action.payload.filterKey] : '';
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: emptyTime },
        filters: { ...filters, [action.payload.filterKey]: emptyTime },
        selectedValues: { ...selectedValues, [action.payload.filterKey]: hasLastSelected ? currentValue : emptyTime }
      };
    },

    SET_DATA_WITH_START_AND_END_DATE_DEFAULT_6_MONTH_AGO: function() {
      const defaultDate6MonthAgo = new moment()
        .subtract(6, 'months')
        .startOf('month')
        .format(yearMonthDayFormat);
      const hasLastSelected = has(lastSelectedData, action.payload.filterKey);
      const currentValue = hasLastSelected ? selectedValues[action.payload.filterKey] : '';
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: defaultDate6MonthAgo },
        filters: { ...filters, [action.payload.filterKey]: defaultDate6MonthAgo },
        selectedValues: {
          ...selectedValues,
          [action.payload.filterKey]: hasLastSelected ? currentValue : defaultDate6MonthAgo
        }
      };
    },

    SET_DATA_WITH_START_AND_END_DATE_DEFAULT_WEEK_AGO: function() {
      const defaultDate1WeekAgo = new moment().subtract(1, 'week').format(yearMonthDayFormat);
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: defaultDate1WeekAgo },
        filters: { ...filters, [action.payload.filterKey]: defaultDate1WeekAgo },
        selectedValues: { ...selectedValues, [action.payload.filterKey]: defaultDate1WeekAgo }
      };
    },

    SET_DATA_WITH_START_AND_END_DATE_DEFAULT_1_YEAR_AGO: function() {
      const defaultDate1YearAgo = new moment().subtract(1, 'year').format(yearMonthDayFormat);
      const hasLastSelected = has(lastSelectedData, action.payload.filterKey);
      const currentValue = hasLastSelected ? selectedValues[action.payload.filterKey] : '';
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: defaultDate1YearAgo },
        filters: { ...filters, [action.payload.filterKey]: defaultDate1YearAgo },
        selectedValues: {
          ...selectedValues,
          [action.payload.filterKey]: hasLastSelected ? currentValue : defaultDate1YearAgo
        }
      };
    },

    SET_DATA_WITH_START_AND_END_DATE_DEFAULT_3_MONTHS_AGO: function() {
      const defaultDate3MonthsAgo = new moment().subtract(3, 'months').format(yearMonthDayFormat);
      const hasLastSelected = has(lastSelectedData, action.payload.filterKey);
      const currentValue = hasLastSelected ? selectedValues[action.payload.filterKey] : '';
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: defaultDate3MonthsAgo },
        filters: { ...filters, [action.payload.filterKey]: defaultDate3MonthsAgo },
        selectedValues: {
          ...selectedValues,
          [action.payload.filterKey]: hasLastSelected ? currentValue : defaultDate3MonthsAgo
        }
      };
    },

    SET_DATA_WITH_START_AND_END_DATE_DEFAULT_BEGINNING_OF_CURRENT_MONTH: function() {
      const defaultDate3MonthsAgo = new moment().startOf('month').format(yearMonthDayFormat);
      return {
        ...state,
        initialData: { ...initialData, [action.payload.filterKey]: defaultDate3MonthsAgo },
        filters: { ...filters, [action.payload.filterKey]: defaultDate3MonthsAgo },
        selectedValues: { ...selectedValues, [action.payload.filterKey]: defaultDate3MonthsAgo }
      };
    },

    ON_STUDY_METRICS_STUDY_CHANGE: function() {
      const therapeutic_areas = filterTherapeuticAreasBasedOnSelectedStudyIds(
        initialData.therapeutic_areas,
        action.payload.value,
        ssus
      );
      return {
        ...state,
        selectedValues: { ...selectedValues, therapeutic_areas, studies: action.payload.value },
        filters: { ...filters, therapeutic_areas }
      };
    },

    ON_SELECT_CHANGE: function() {
      return {
        ...state,
        selectedValues: { ...selectedValues, [action.payload.key]: action.payload.value }
      };
    },

    ON_SELECT_DATE_CHANGE: function() {
      return {
        ...state,
        selectedValues: { ...selectedValues, [action.payload.key]: action.payload.value.format(DATE_FORMAT) }
      };
    },

    ON_SELECT_DATE_CLEAR: function() {
      return {
        ...state,
        selectedValues: { ...selectedValues, [action.payload.key]: '' }
      };
    },

    CLEAR_DATA: function() {
      const favoriteOptions = state.favoriteOptions ? { ...state.favoriteOptions } : {};
      return {
        initialData: {},
        filters: {},
        selectedValues: {},
        ssus,
        favoriteOptions
      };
    },

    ON_STUDY_SELECT_CHANGE: function() {
      const sites = filterSitesByStudyIdsAndCanceledSsus(action.payload.value, initialData?.sites, ssus);
      const ssu_status = selectedValues.ssu_status;
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites, ssu_status },
        filters: { ...filters, sites }
      };
    },

    ON_STUDY_MULTISELECT_CHANGE: function() {
      const sites = filterSitesByStudyIdsAndSsus(state.ssus, action.payload.value, state.initialData.sites);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites },
        filters: { ...state.filters, ...{ sites: sites } }
      };
    },

    ON_STUDY_MULTISELECT_CHANGE_NON_FILTERED: function() {
      const sites = filterSitesByStudyIdsAndSsus(state.ssus, action.payload.value, state.initialData.sites);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites },
        filters: { ...state.filters }
      };
    },

    ON_STUDY_MULTISELECT_CHANGE_FILTER_CANCELED_SSUS: function() {
      const sites = filterSitesByStudyIdsAndCanceledSsus(action.payload.value, state.initialData.sites, state.ssus);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites },
        filters: { ...state.filters, ...{ sites: sites } }
      };
    },

    ON_SITE_SELECT_CHANGE: function() {
      const studies = filterStudiesBasedOnSiteIds(initialData.studies, action.payload.value, state.ssus);
      return {
        ...state,
        selectedValues: { ...selectedValues, sites: action.payload.value, studies },
        filters: { ...filters, studies }
      };
    },

    ON_SITE_SELECT_STUDIES: function() {
      const studies = filterStudiesBasedOnSiteIds(initialData.studies, action.payload.value, state.ssus);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies, sites: action.payload.value },
        filters: { ...filters, studies }
      };
    },

    ON_SITE_SELECT_STUDIES_NON_FILTERED: function() {
      const studies = filterStudiesBasedOnSiteIds(initialData.studies, action.payload.value, state.ssus);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies, sites: action.payload.value },
        filters: { ...filters }
      };
    },

    ON_STUDY_CHANGE_WITH_THERAPEUTIC_AREAS: function() {
      const sites = filterSitesByStudyIdsAndSsus(ssus, action.payload.value, initialData.sites);
      const therapeutic_areas = filterTherapeuticAreasBasedOnSelectedStudyIds(
        initialData.therapeutic_areas,
        action.payload.value,
        ssus
      );
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites, therapeutic_areas },
        filters: { ...filters, sites, therapeutic_areas }
      };
    },

    ON_STUDY_CHANGE_WITH_THERAPEUTIC_AREAS_FILTER_CANCELED_SSUS: function() {
      const sites = filterSitesByStudyIdsAndCanceledSsus(action.payload.value, initialData.sites, ssus);
      const therapeutic_areas = filterTherapeuticAreasBasedOnSelectedStudyIds(
        initialData.therapeutic_areas,
        action.payload.value,
        ssus
      );
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites, therapeutic_areas },
        filters: { ...filters, sites, therapeutic_areas }
      };
    },

    ON_STUDY_CHANGE_WITH_SITE: function() {
      const sites = filterSitesByStudyIdsAndSsus(state.ssus, action.payload.value, state.initialData.sites);
      return {
        ...state,
        selectedValues: { ...selectedValues, studies: action.payload.value, sites },
        filters: { ...filters, sites }
      };
    },

    SET_FAVORITE_OPTIONS_FOR_FILTER: function() {
      const { filterKey, favOptions } = action.payload;
      return setIn(state, ['favoriteOptions', filterKey], favOptions);
    },

    SET_FAVORITE_OPTIONS: function() {
      return {
        ...state,
        favoriteOptions: action.payload
      };
    }
  };

  const reducerFunction = reducers[action.type];
  return reducerFunction ? reducerFunction() : state;
}

function prepareModeParameters(modeParameters) {
  const { initialData, lastSelectedData } = splitModeParameters(modeParameters);
  return {
    initialData: { ...initialData },
    lastSelectedData: { ...lastSelectedData },
    filters: { ...initialData },
    selectedValues: getSelectedValues(initialData, lastSelectedData)
  };
}

function splitModeParameters(modeParameters) {
  return reduce(
    modeParameters,
    function(accumulator, value, key) {
      if (key.includes('last_selected_')) {
        accumulator.lastSelectedData[key.replace('last_selected_', '')] = value;
      } else {
        accumulator.initialData[key] = value;
      }
      return accumulator;
    },
    {
      initialData: {},
      lastSelectedData: {}
    }
  );
}

function getSelectedValues(initialData, lastSelectedData) {
  return reduce(
    initialData,
    function(accumulator, value, key) {
      const lastSelected = lastSelectedData[key];
      if (['start_date', 'end_date'].includes(key)) {
        if (!isEmpty(lastSelected) && !isEmpty(lastSelected[0])) {
          accumulator[key] = moment(lastSelected[0]).format(yearMonthDayFormat);
        } else {
          accumulator[key] = '';
        }
      } else {
        accumulator[key] = !isEmpty(lastSelected)
          ? filter(value, function({ id }) {
              return lastSelected.includes(id);
            })
          : value;
      }
      return accumulator;
    },
    {}
  );
}
